/**
 * « Le travail de la semaine » — combien de compétences, et combien de temps.
 *
 * Demandé par Hedi le 20 août 2026. Ce sont des **faits sur le travail fourni**,
 * pas un score : `mastery` reste seul à dire où en est la maîtrise, et cette
 * carte ne prétend rien sur le niveau.
 *
 * Sa place est la profondeur du §6.2, pas la réponse en cinq secondes du §6.1 :
 * un parent ouvre l'application pour savoir « est-ce que ça va ? », et le temps
 * passé répond à une autre question.
 *
 * **La référence est affichée à côté de la durée quand elle existe**, et ce
 * n'est pas décoratif. Douze minutes ne veulent rien dire seules ; « 12 min pour
 * une référence de 30 s par exercice » dit qu'il s'est passé autre chose qu'un
 * calcul. La même valeur sert à `mastery` pour la fluence — sans elle, un chiffre
 * nu invite à conclure sur l'enfant, et le contrat interdit de mettre la lenteur
 * en accusation.
 */

import type { WorkDone, WorkedCompetency } from "@/api/schema";
import { subjectLabel } from "@/config/wording";
import { Card, Empty } from "@/ui/primitives";

/** Une durée en secondes, dite comme un humain la dit. */
export function humanDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) return null;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste === 0 ? `${hours} h` : `${hours} h ${String(reste).padStart(2, "0")}`;
}

/**
 * Ce qu'on écrit sous une ligne : le nombre de tentatives, et la référence quand
 * on la connaît.
 *
 * Rendu comme fonction pure parce que c'est la seule partie qui décide de
 * quelque chose — le reste est du balisage, et un test de rendu mesurerait React.
 */
export function foot(entry: WorkedCompetency): string {
  const essais = entry.attempts === 1 ? "1 exercice" : `${entry.attempts} exercices`;
  const reference = entry.reference_duration_s;
  if (!reference) return essais;
  return `${essais} · référence ${humanDuration(reference)} par exercice`;
}

export function WorkDoneCard({ work }: { work: WorkDone | null | undefined }) {
  const entries = work?.competencies ?? [];
  const total = humanDuration(work?.total_duration_s);

  return (
    <Card title="Le travail de la semaine">
      {entries.length === 0 ? (
        <Empty>Aucun exercice rendu ces sept derniers jours.</Empty>
      ) : (
        <>
          <p className="muted small">
            {entries.length === 1
              ? "1 compétence travaillée"
              : `${entries.length} compétences travaillées`}
            {total ? ` · ${total} en tout` : null}
          </p>
          <ul className="work-list">
            {entries.map((entry) => (
              <li key={entry.competency_id} className="work">
                <div className="work__head">
                  <span className="work__label">{entry.label}</span>
                  <span className="work__time">{humanDuration(entry.duration_s) ?? "—"}</span>
                </div>
                <p className="muted small">
                  {subjectLabel(entry.subject)} · {foot(entry)}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
